import * as React from "react";
import * as XLSX from "xlsx";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertCircle, CheckCircle2, Download, GitCompareArrows, Loader2, Pencil, RefreshCw, Search, ShieldAlert } from "lucide-react";
import { useAuth } from "@/supabase/AuthContext";
import { createBrowserSupabase } from "@/supabase/client";
import { matchInvoiceProduct } from "@/features/besiktas/invoiceProductMatching";
import { rankProductCandidates } from "@/features/nebim/productCandidateMatching";
import { matchProductsWithAI, type AiProductMatchProposal } from "@/lib/ai";

type Batch = {
  id: string;
  original_filename: string | null;
  status: string;
  import_type: string;
  created_at: string;
  total_rows: number | null;
  error_summary: unknown;
};

type ImportRow = {
  id: string;
  batch_id: string;
  source_row_number: number;
  row_status: string;
  source_product_code: string | null;
  source_barcode: string | null;
  normalized_payload: Record<string, unknown> | null;
};

type Product = { id: string; product_code: string; product_name: string | null };
type ApprovedMapping = { id: string; source_product_code: string; source_barcode: string | null; source_product_name: string | null; source_color_code: string | null; source_size_code: string | null; product_id: string | null; product_variant_id: string | null; updated_at: string | null };

type PendingItem = {
  key: string;
  batch: Batch;
  row: ImportRow;
  code: string;
  barcode: string;
  name: string;
  colorCode: string;
  colorName: string;
  sizeCode: string;
  candidates: Product[];
};

const PAGE_SIZE = 1000;
const MAX_BATCHES = 20;

function normalized(value: string): string {
  return value.toLocaleLowerCase("tr-TR").replace(/[^a-z0-9çğıöşü]/g, "");
}


function displayValue(value: unknown): string {
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

// Cost/profit analysis is product-level: color, size and barcode never split a mapping.
function mappingKey(code: string, _colorCode = "", _sizeCode = "", name = ""): string {
  // Name-only rows receive a stable internal source key so separate descriptions
  // can be approved without colliding on an empty SKU.
  return normalized(code) || (name.trim() ? "name:" + normalized(name) : "");
}

function candidatesFor(code: string, name: string, products: Product[]): Product[] {
  // Code and normalized product-name suggestions are intentionally advisory.
  // The operator must approve the product and, when present, its exact variant.
  return rankProductCandidates({ code, name }, products, 5).map((item) => item.product);
}

const ProductMatching: React.FC = () => {
  const { companyId } = useAuth();
  const factory = React.useMemo(() => createBrowserSupabase(), []);
  const [items, setItems] = React.useState<PendingItem[]>([]);
  const [productCatalog, setProductCatalog] = React.useState<Product[]>([]);
  const [invoiceDraft, setInvoiceDraft] = React.useState<{ invoice: any; testOnly?: boolean } | null>(null);
  const [editingInvoiceLineNo, setEditingInvoiceLineNo] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  const [lastLoaded, setLastLoaded] = React.useState<string | null>(null);
  const [savingKey, setSavingKey] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [aiMatching, setAiMatching] = React.useState(false);
  const [aiError, setAiError] = React.useState<string | null>(null);
  const [aiProposal, setAiProposal] = React.useState<AiProductMatchProposal | null>(null);
  const [invoiceAiBusyLine, setInvoiceAiBusyLine] = React.useState<number | null>(null);
  const [invoiceAiProposals, setInvoiceAiProposals] = React.useState<Record<number, AiProductMatchProposal>>({});
  const [invoiceAiErrors, setInvoiceAiErrors] = React.useState<Record<number, string>>({});
  const [approvedMappings, setApprovedMappings] = React.useState<ApprovedMapping[]>([]);
  const [productCodeById, setProductCodeById] = React.useState<Map<string, string>>(new Map());
  const [savedInvoices, setSavedInvoices] = React.useState<any[]>([]);
  const [savedInvoiceError, setSavedInvoiceError] = React.useState<string | null>(null);
  const [autoMatchSummary, setAutoMatchSummary] = React.useState<string | null>(null);
  const [autoMatchingInvoices, setAutoMatchingInvoices] = React.useState(false);
  const autoMatchAttemptedRef = React.useRef<Set<string>>(new Set());
  const autoAiAttemptedRef = React.useRef<Set<string>>(new Set());
  const invoiceDraftAiAttemptedRef = React.useRef<Set<string>>(new Set());
  const loadRequestId = React.useRef(0);

  const load = React.useCallback(async () => {
    const requestId = ++loadRequestId.current;
    if (!factory.ok || !companyId) {
      setItems([]); setApprovedMappings([]); setProductCodeById(new Map());
      setSelectedKey(null); setLastLoaded(null); setError(null); setLoading(false);
      return;
    }
    setLoading(true); setError(null); setSuccess(null);
    try {
      // Include all committed sales/stock batches, including user-authorized
      // sample files such as AFYON, so they can be tested end-to-end.
      const batches: Batch[] = [];
      const BATCH_PAGE_SIZE = 500;
      const BATCH_SCAN_CAP = 10000;
      let batchScanCount = 0;
      let batchesComplete = false;
      for (let start = 0; start < BATCH_SCAN_CAP; start += BATCH_PAGE_SIZE) {
        const { data, error: batchError } = await factory.client.from("import_batches")
          .select("id, original_filename, status, import_type, created_at, total_rows, error_summary")
          .eq("company_id", companyId)
          .in("import_type", ["nebim_sales_daily", "nebim_stock_snapshot"])
          .eq("status", "committed")
          .order("created_at", { ascending: false })
          .range(start, start + BATCH_PAGE_SIZE - 1);
        if (requestId !== loadRequestId.current) return;
        if (batchError) throw batchError;
        const page = (data ?? []) as Batch[];
        batchScanCount += page.length;
        for (const batch of page) {
          batches.push(batch);
          if (batches.length >= MAX_BATCHES) break;
        }
        if (batches.length >= MAX_BATCHES) break;
        if (page.length < BATCH_PAGE_SIZE) { batchesComplete = true; break; }
      }
      if (!batchesComplete && batches.length < MAX_BATCHES && batchScanCount >= BATCH_SCAN_CAP) {
        throw new Error("Son 10.000 aktarım tarandı; eşleştirme kuyruğunun eksiksiz olduğu doğrulanamadı. Eksik sonuç göstermemek için işlem durduruldu.");
      }

      // Load every approved mapping in bounded pages; a capped partial set could
      // otherwise make an already-mapped source code appear unmatched.
      const approved: ApprovedMapping[] = [];
      const MAPPING_SCAN_CAP = 100000;
      let mappingsComplete = false;
      for (let start = 0; start < MAPPING_SCAN_CAP; start += PAGE_SIZE) {
        const { data, error: mappingError } = await factory.client.from("product_mappings")
          .select("id, source_product_code, source_barcode, source_product_name, source_color_code, source_size_code, product_id, product_variant_id, updated_at")
          .eq("company_id", companyId).eq("source_system", "nebim").eq("status", "approved")
          .order("updated_at", { ascending: false })
          .range(start, start + PAGE_SIZE - 1);
        if (requestId !== loadRequestId.current) return;
        if (mappingError) throw mappingError;
        const page = (data ?? []) as ApprovedMapping[];
        approved.push(...page);
        if (page.length < PAGE_SIZE) { mappingsComplete = true; break; }
      }
      if (!mappingsComplete) throw new Error("100.000 onaylı eşleme sınırına ulaşıldı. Eksik eşleme verisiyle kuyruk oluşturulmadı.");
      // A partial product catalogue must not produce false unmatched-SKU alerts.
      const products: Product[] = [];
      const PRODUCT_SCAN_CAP = 100000;
      let productsComplete = false;
      for (let start = 0; start < PRODUCT_SCAN_CAP; start += PAGE_SIZE) {
        const { data, error: productError } = await factory.client.from("products")
          .select("id, product_code, product_name").eq("company_id", companyId).eq("is_active", true)
          .order("product_code", { ascending: true })
          .range(start, start + PAGE_SIZE - 1);
        if (requestId !== loadRequestId.current) return;
        if (productError) throw productError;
        const page = (data ?? []) as Product[];
        products.push(...page.filter((product) => !!product.product_code));
        if (page.length < PAGE_SIZE) { productsComplete = true; break; }
      }
      if (!productsComplete) throw new Error("100.000 ürün sınırına ulaşıldı. Eksik ürün kataloğuyla yanlış eşleştirme önermekten kaçınmak için işlem durduruldu.");
      // Treat a saved mapping as valid only when its target product is active and,
      // when a variant was selected, that variant is still active and belongs to the product.
      // Stale mappings must reappear in the review queue instead of silently hiding errors.
      const activeProductIds = new Set(products.map((product) => product.id));
      const approvedProductsBySku = new Map<string, Set<string>>();
      for (const mapping of approved) {
        const skuKey = mappingKey(mapping.source_product_code ?? "");
        if (!skuKey || !mapping.product_id || !activeProductIds.has(mapping.product_id)) continue;
        const productIds = approvedProductsBySku.get(skuKey) ?? new Set<string>();
        productIds.add(mapping.product_id);
        approvedProductsBySku.set(skuKey, productIds);
      }
      // Hide a source SKU only when all its approved rows resolve to one active product.
      const approvedKeys = new Set(
        [...approvedProductsBySku.entries()]
          .filter(([, productIds]) => productIds.size === 1)
          .map(([sku]) => sku),
      );
      setApprovedMappings(approved);
      setProductCatalog(products);
      setProductCodeById(new Map(products.map((product) => [product.id, product.product_code])));
      if (batches.length === 0) {
        setItems([]); setSelectedKey(null); setLastLoaded(new Date().toISOString());
        return;
      }

      const found: PendingItem[] = [];
      for (const batch of batches) {
        const collected: ImportRow[] = [];
        let rowsComplete = false;
        for (let start = 0; start < 20000; start += PAGE_SIZE) {
          const { data, error: rowsError } = await factory.client.from("import_rows")
            .select("id, batch_id, source_row_number, row_status, source_product_code, source_barcode, normalized_payload")
            .eq("company_id", companyId).eq("batch_id", batch.id)
            .order("source_row_number", { ascending: true })
            .range(start, start + PAGE_SIZE - 1);
          if (requestId !== loadRequestId.current) return;
          if (rowsError) throw rowsError;
          const page = (data ?? []) as ImportRow[];
          collected.push(...page);
          if (page.length < PAGE_SIZE) { rowsComplete = true; break; }
        }
        if (!rowsComplete) throw new Error("Bir aktarım 20.000 satır sınırına ulaştı. Eksik satırlarla eşleştirme kuyruğu gösterilmedi.");
        for (const row of collected) {
          if (row.row_status === "error") continue;
          const payload = row.normalized_payload ?? {};
          const code = row.source_product_code?.trim() || displayValue(payload.sku).trim() || displayValue(payload.product_code).trim();
          const barcode = row.source_barcode?.trim() || displayValue(payload.barcode).trim();
          const name = displayValue(payload.product_name) || displayValue(payload.name);
          if (!code && !barcode && name.trim().length < 3) continue;
          const normalizedCode = normalized(code);
          const sourceIdentityKey = mappingKey(code, "", "", name);
          const colorCode = displayValue(payload.color_code).trim();
          const colorName = displayValue(payload.color_name).trim();
          const sizeCode = displayValue(payload.size_code).trim() || displayValue(payload.size).trim();
          if (approvedKeys.has(sourceIdentityKey)) continue;
          // Product identity is sufficient for cost and profitability analysis.
          // Color, size and barcode are intentionally ignored for product-level mapping.
          if (code && products.some((product) => normalized(product.product_code) === normalizedCode)) continue;
          found.push({ key: row.id, batch, row, code, barcode, name, colorCode, colorName, sizeCode, candidates: candidatesFor(code, name, products) });
        }
      }
      const unique = new Map<string, PendingItem>();
      for (const item of found) {
        const identity = [mappingKey(item.code, item.colorCode, item.sizeCode, item.name), normalized(item.barcode), normalized(item.name)].join("|");
        if (!unique.has(identity)) unique.set(identity, item);
      }
      const next = Array.from(unique.values()).sort((a, b) => b.batch.created_at.localeCompare(a.batch.created_at));
      if (requestId !== loadRequestId.current) return;
      setItems(next);
      setSelectedKey((current) => next.some((item) => item.key === current) ? current : next[0]?.key ?? null);
      setLastLoaded(new Date().toISOString());
    } catch (e) {
      if (requestId !== loadRequestId.current) return;
      const err = e as { message?: unknown };
      setItems([]); setApprovedMappings([]); setProductCodeById(new Map()); setSelectedKey(null); setLastLoaded(null);
      setError(typeof err?.message === "string" ? err.message : "Ürün eşleştirme kuyruğu yüklenemedi.");
    } finally {
      if (requestId === loadRequestId.current) setLoading(false);
    }
  }, [factory, companyId]);

  const persistInvoiceDraft = React.useCallback(async (draft: { invoice: any; testOnly?: boolean }) => {
    if (!companyId || !draft.invoice?.file_hash || !draft.invoice?.file_name) {
      throw new Error("Fatura taslağında dosya adı veya SHA-256 özeti eksik.");
    }
    if (!factory.ok) throw new Error("Supabase bağlantısı yapılandırılmamış.");
    const { error: saveError } = await factory.client.rpc("save_supplier_invoice_working_draft", {
      p_company_id: companyId,
      p_file_sha256: draft.invoice.file_hash,
      p_file_name: draft.invoice.file_name,
      p_invoice_payload: draft.invoice,
      p_test_only: Boolean(draft.testOnly),
    });
    if (saveError) throw saveError;
  }, [companyId, factory]);

  React.useEffect(() => {
    if (!companyId || !factory.ok) { setInvoiceDraft(null); return; }
    let cancelled = false;
    const requestedCompanyId = companyId;
    void (async () => {
      try {
        const { data, error: draftError } = await factory.client.rpc("get_supplier_invoice_working_draft", { p_company_id: requestedCompanyId });
        if (cancelled) return;
        if (draftError) throw draftError;
        const saved = data as { invoice?: any; test_only?: boolean } | null;
        setInvoiceDraft(saved?.invoice && Array.isArray(saved.invoice.lines)
          ? { invoice: saved.invoice, testOnly: Boolean(saved.test_only) }
          : null);
      } catch (e) {
        if (!cancelled) setError("Kalıcı fatura taslağı yüklenemedi: " + ((e as { message?: string }).message ?? "Bilinmeyen hata"));
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, factory]);

  const requestInvoiceLineAiMatch = async (line: any) => {
    if (!companyId || invoiceAiBusyLine != null) return;
    setInvoiceAiBusyLine(line.line_no);
    setInvoiceAiErrors((current) => ({ ...current, [line.line_no]: "" }));
    setInvoiceAiProposals((current) => { const next = { ...current }; delete next[line.line_no]; return next; });
    const result = await matchProductsWithAI([{
      sourceId: String(line.line_no),
      code: typeof line.product_code === "string" ? line.product_code : "",
      name: typeof line.product_name === "string" ? line.product_name : "",
      quantity: Number(line.qty ?? line.quantity ?? 0),
      unitCost: Number(line.unit_cost ?? line.unit_price ?? line.price ?? 0),
    }], companyId);
    if (!result.ok) {
      setInvoiceAiErrors((current) => ({ ...current, [line.line_no]: result.message }));
    } else {
      const proposal = result.matches.find((item) => item.sourceId === String(line.line_no));
      if (proposal) setInvoiceAiProposals((current) => ({ ...current, [line.line_no]: proposal }));
      else setInvoiceAiErrors((current) => ({ ...current, [line.line_no]: "AI güvenilir aday bulamadı; eşleşme yapılmadı." }));
    }
    setInvoiceAiBusyLine(null);
  };

  const assignInvoiceLine = React.useCallback((lineNo: number, productCode: string) => {
    if (!companyId || !invoiceDraft?.invoice) return;
    const nextInvoice = {
      ...invoiceDraft.invoice,
      lines: invoiceDraft.invoice.lines.map((line: any) => line.line_no === lineNo
        ? { ...line, matched_product_code: productCode || null, matched_product_variant_id: null, match: productCode ? "found" : "unknown" }
        : line),
    };
    const nextDraft = { ...invoiceDraft, invoice: nextInvoice, savedAt: new Date().toISOString() };
    setInvoiceDraft(nextDraft);
    void persistInvoiceDraft(nextDraft).then(() => {
      setSuccess(`Fatura ${nextInvoice.meta.invoice_no ?? ""} — ${lineNo}. kalem eşleştirme taslağı sunucuya kaydedildi. Henüz fatura kaydedilmedi.`);
    }).catch((e) => {
      setError("Fatura eşleştirmesi kalıcı kaydedilemedi: " + ((e as { message?: string }).message ?? "Bilinmeyen hata"));
    });
  }, [companyId, invoiceDraft, persistInvoiceDraft]);

  // Invoice matching uses deterministic evidence first, then AI as a second independent check.
  // Existing user choices are never overwritten; only AI + exact-identity agreements auto-save.
  const autoMatchInvoiceDraft = React.useCallback(async () => {
    if (!companyId || !invoiceDraft?.invoice || productCatalog.length === 0) return;
    let nextLines = invoiceDraft.invoice.lines.map((line: any) => {
      if (line.matched_product_code) return line;
      const match = matchInvoiceProduct(
        { product_code: line.product_code, product_name: line.product_name },
        productCatalog,
      );
      if (!match) return line;
      return {
        ...line,
        matched_product_code: match.product.product_code,
        matched_product_variant_id: null,
        match: "found",
        match_confidence: match.confidence,
        match_method: match.method,
        match_note: "Tekil ürün kodu/model kodu eşleşmesi; maliyet ana ürün düzeyinde hesaplanacak",
      };
    });

    const unresolved = nextLines.filter((line: any) => !line.matched_product_code);
    const aiEligible = unresolved.filter((line: any) => {
      const key = `${invoiceDraft.invoice.file_hash}:${line.line_no}`;
      if (invoiceDraftAiAttemptedRef.current.has(key)) return false;
      invoiceDraftAiAttemptedRef.current.add(key);
      return true;
    }).slice(0, 40);
    if (aiEligible.length) {
      const aiResult = await matchProductsWithAI(aiEligible.map((line: any) => ({
        sourceId: String(line.line_no),
        code: typeof line.product_code === "string" ? line.product_code : "",
        name: typeof line.product_name === "string" ? line.product_name : "",
        quantity: Number(line.qty ?? line.quantity ?? 0),
        unitCost: Number(line.unit_cost ?? line.unit_price ?? line.price ?? 0),
      })), companyId);
      if (aiResult.ok) {
        nextLines = nextLines.map((line: any) => {
          if (line.matched_product_code) return line;
          const proposal = aiResult.matches.find((item) => item.sourceId === String(line.line_no));
          if (!proposal?.autoMatch || proposal.reviewRequired || !proposal.productId) return line;
          const candidate = productCatalog.find((product) => product.id === proposal.productId);
          if (!candidate) return line;
          return {
            ...line,
            matched_product_code: candidate.product_code,
            matched_product_variant_id: null,
            match: "found",
            match_confidence: proposal.confidence,
            match_method: "ai_auto",
            match_note: `Sistem ve Cloudflare AI tekil ürün eşleşmesinde anlaştı: ${proposal.reason}`,
          };
        });
      } else {
        aiEligible.forEach((line: any) => invoiceDraftAiAttemptedRef.current.delete(`${invoiceDraft.invoice.file_hash}:${line.line_no}`));
      }
    }

    const matchedCount = nextLines.filter((line: any, index: number) =>
      !invoiceDraft.invoice.lines[index].matched_product_code && line.matched_product_code
    ).length;
    if (matchedCount === 0) return;
    const nextDraft = {
      ...invoiceDraft,
      invoice: { ...invoiceDraft.invoice, lines: nextLines },
      savedAt: new Date().toISOString(),
    };
    setInvoiceDraft(nextDraft);
    try {
      await persistInvoiceDraft(nextDraft);
      setSuccess(`${matchedCount} güvenilir ürün eşleşmesi otomatik kaydedildi. Belirsiz satırlar inceleme için bırakıldı.`);
    } catch (e) {
      setError("Otomatik eşleşmeler kalıcı kaydedilemedi: " + ((e as { message?: string }).message ?? "Bilinmeyen hata"));
    }
  }, [companyId, invoiceDraft, productCatalog, persistInvoiceDraft]);

  React.useEffect(() => {
    autoMatchInvoiceDraft();
  }, [autoMatchInvoiceDraft]);


  React.useEffect(() => {
    void load();
    return () => { loadRequestId.current += 1; };
  }, [load]);

  // Load saved, unfinalized real supplier invoices separately from the temporary working draft.
  // Matching here writes only product/variant mapping fields through a guarded RPC.
  const loadSavedInvoices = React.useCallback(async () => {
    if (!factory.ok || !companyId) { setSavedInvoices([]); return; }
    setSavedInvoiceError(null);
    try {
      const { data: invoiceData, error: invoiceError } = await factory.client.from("supplier_invoices")
        .select("id,invoice_no,invoice_date,supplier_name,status,test_only,grand_total,created_at")
        .eq("company_id", companyId).eq("test_only", false)
        .in("status", ["draft", "needs_mapping", "ready_to_post"])
        .order("created_at", { ascending: false }).limit(100);
      if (invoiceError) throw invoiceError;
      const invoices = (invoiceData ?? []) as any[];
      if (invoices.length === 0) { setSavedInvoices([]); return; }
      const { data: lineData, error: lineError } = await factory.client.from("supplier_invoice_lines")
        .select("id,invoice_id,line_number,source_product_code,product_name,quantity,product_id,variant_id,mapping_status")
        .eq("company_id", companyId).in("invoice_id", invoices.map((invoice) => invoice.id))
        .order("line_number", { ascending: true });
      if (lineError) throw lineError;
      const grouped = new Map<string, any[]>();
      for (const line of (lineData ?? []) as any[]) {
        const group = grouped.get(line.invoice_id) ?? [];
        group.push(line);
        grouped.set(line.invoice_id, group);
      }
      setSavedInvoices(invoices.map((invoice) => ({ ...invoice, lines: grouped.get(invoice.id) ?? [] })));
    } catch (e) {
      setSavedInvoiceError((e as { message?: string }).message ?? "Kayıtlı faturalar yüklenemedi.");
    }
  }, [factory, companyId]);

  React.useEffect(() => { void loadSavedInvoices(); }, [loadSavedInvoices]);

  // Automatically persist unique, high-confidence product matches.
  // Costing is product-level; color/size variants are intentionally optional.
  React.useEffect(() => {
    if (!savedInvoices.length || !productCatalog.length || !factory.ok || !companyId) return;
    let cancelled = false;
    const pending = savedInvoices.filter((invoice) => (invoice.lines ?? []).some((line: any) => !line.product_id));
    if (!pending.length) {
      setAutoMatchSummary("Tüm kayıtlı fatura kalemleri ürün düzeyinde eşleşmiş.");
      return;
    }
    const attemptKey = pending.map((invoice) => invoice.id).sort().join("|") + ":" + productCatalog.length;
    if (autoMatchAttemptedRef.current.has(attemptKey)) return;
    autoMatchAttemptedRef.current.add(attemptKey);
    setAutoMatchingInvoices(true);
    setSavedInvoiceError(null);
    void (async () => {
      let matched = 0;
      let unresolved = 0;
      const reasons: string[] = [];
      const appliedProductByLine = new Map<string, string>();
      try {
        for (const invoice of pending) {
          const mappings: Array<{ invoice_line_id: string; product_id: string; variant_id: null }> = [];
          const unmatchedLines: any[] = [];
          for (const line of invoice.lines ?? []) {
            if (line.product_id) continue;
            const match = matchInvoiceProduct({ product_code: line.source_product_code, product_name: line.product_name }, productCatalog);
            if (match) {
              mappings.push({ invoice_line_id: line.id, product_id: match.product.id, variant_id: null });
              appliedProductByLine.set(line.id, match.product.id);
            } else unmatchedLines.push(line);
          }

          // AI is the second-stage resolver for lines the deterministic matcher cannot prove.
          // Only the server's strict unique exact-identity + >=95% agreement flag is accepted.
          const aiLines = unmatchedLines.slice(0, 40);
          if (aiLines.length) {
            const aiResult = await matchProductsWithAI(aiLines.map((line: any) => ({
              sourceId: line.id,
              code: typeof line.source_product_code === "string" ? line.source_product_code : "",
              name: typeof line.product_name === "string" ? line.product_name : "",
              quantity: Number(line.quantity ?? 0),
            })), companyId);
            if (aiResult.ok) {
              for (const proposal of aiResult.matches) {
                if (!proposal.autoMatch || proposal.reviewRequired || !proposal.productId) continue;
                const candidate = productCatalog.find((product) => product.id === proposal.productId);
                const line = aiLines.find((item: any) => item.id === proposal.sourceId);
                if (!candidate || !line) continue;
                mappings.push({ invoice_line_id: line.id, product_id: candidate.id, variant_id: null });
                appliedProductByLine.set(line.id, candidate.id);
              }
            }
          }

          const resolvedLineIds = new Set(mappings.map((mapping) => mapping.invoice_line_id));
          for (const line of unmatchedLines) {
            if (resolvedLineIds.has(line.id)) continue;
            unresolved += 1;
            reasons.push(`${line.product_name || line.source_product_code || `Satır ${line.line_number}`}: fatura ve envanter/katalog verilerinde tekil güvenilir eşleşme bulunamadı`);
          }
          if (!mappings.length) continue;
          const { error: saveError } = await factory.client.rpc("set_supplier_invoice_line_product_mappings", {
            p_company_id: companyId, p_invoice_id: invoice.id, p_mappings: mappings,
          });
          if (saveError) throw saveError;
          matched += mappings.length;
        }
        if (cancelled) return;
        setAutoMatchSummary(`${matched} ürün otomatik eşleştirildi ve kaydedildi; ${unresolved} kalem eşleşmedi.`);
        if (unresolved) setSavedInvoiceError("Kontrol gereken kalemler: " + reasons.slice(0, 8).join(" • ") + (reasons.length > 8 ? ` • ve ${reasons.length - 8} kalem daha` : ""));
        // Update the view from confirmed RPC results rather than refetching inside
        // this effect (which would cancel its own cleanup and leave the spinner on).
        setSavedInvoices((current) => current.map((invoice) => ({
          ...invoice,
          lines: (invoice.lines ?? []).map((line: any) => appliedProductByLine.has(line.id)
            ? { ...line, product_id: appliedProductByLine.get(line.id), variant_id: null, mapping_status: "manual" }
            : line),
        })));
        setAutoMatchingInvoices(false);
      } catch (e) {
        if (cancelled) return;
        autoMatchAttemptedRef.current.delete(attemptKey);
        setSavedInvoiceError("Otomatik eşleştirme kaydedilemedi: " + ((e as { message?: string }).message ?? "Bilinmeyen hata"));
      } finally {
        if (!cancelled) setAutoMatchingInvoices(false);
      }
    })();
    return () => { cancelled = true; };
  }, [savedInvoices, productCatalog, factory, companyId, loadSavedInvoices]);

  const approveMapping = React.useCallback(async (item: PendingItem, product: Product, matchMethod: "manual" | "ai_auto" = "manual", confidence = 1, matchReason = "Ürün Eşleştirme ekranında kullanıcı tarafından onaylandı.") => {
    if (!factory.ok || !companyId || savingKey) return;
    setSavingKey(item.key);
    setError(null);
    setSuccess(null);
    try {
      // Variant selection is deliberately not required for profitability analysis.
      // Store only the parent product so all sizes/colors share one verified cost basis.
      const productLevelVariantId: string | null = null;
      // The complete approved mapping list is already loaded and capped fail-closed.
      // Match case/whitespace-insensitively just like the database uniqueness index,
      // rather than relying on exact PostgREST equality and accidentally attempting a duplicate insert.
      const existing = approvedMappings.find((entry) =>
        mappingKey(entry.source_product_code ?? "", entry.source_color_code ?? "", entry.source_size_code ?? "", entry.source_product_name ?? "") ===
        mappingKey(item.code, item.colorCode, item.sizeCode, item.name)
      ) ?? null;
      const mapping = {
        company_id: companyId, source_system: "nebim", source_product_code: item.code || ("NAME:" + normalized(item.name)),
        source_barcode: null, source_product_name: item.name || null,
        // Cost matching is product-level; omit variant dimensions when no variant is selected.
        source_color_code: null, source_color_name: null, source_size_code: null,
        product_id: product.id, product_variant_id: productLevelVariantId, match_method: matchMethod,
        status: "approved", confidence: matchMethod === "ai_auto" ? confidence : 1,
        notes: matchMethod === "ai_auto" ? `Sistem + Cloudflare AI otomatik eşleştirmesi: ${matchReason}` : matchReason,
        updated_at: new Date().toISOString(),
      };
      const result = existing?.id
        ? await factory.client.from("product_mappings").update(mapping).eq("id", existing.id).eq("company_id", companyId).select("id").maybeSingle()
        : await factory.client.from("product_mappings").insert(mapping).select("id").single();
      if (result.error) throw result.error;
      if (!result.data?.id) throw new Error("Eşleme kaydı doğrulanamadı; yetki veya kayıt kuralı engellemiş olabilir.");
      setApprovedMappings((current) => [
        { id: result.data!.id, source_product_code: item.code || ("NAME:" + normalized(item.name)), source_barcode: null, source_product_name: item.name || null, source_color_code: null, source_size_code: null, product_id: product.id, product_variant_id: productLevelVariantId, updated_at: mapping.updated_at },
        ...current.filter((entry) => entry.id !== result.data!.id && mappingKey(entry.source_product_code, entry.source_color_code ?? "", entry.source_size_code ?? "", entry.source_product_name ?? "") !== mappingKey(item.code, item.colorCode, item.sizeCode, item.name)),
      ]);
      setItems((current) => current.filter((candidate) => candidate.key !== item.key));
      setSelectedKey((current) => current === item.key ? null : current);
      
      setSuccess((item.code ? item.code + " kodu" : '"' + item.name + '" ürün adı') + " " + product.product_code + (matchMethod === "ai_auto" ? " ürüne sistem + AI tarafından otomatik eşlendi." : " ürüne maliyet analizi için kalıcı olarak eşlendi.") + " Beden/renk ayrımı yapılmadı; geçmiş satış/stok kayıtları geriye dönük değiştirilmedi.");
    } catch (e) {
      const err = e as { message?: unknown };
      setError(typeof err?.message === "string" ? err.message : "Eşleme kaydedilemedi.");
    } finally { setSavingKey(null); }
  }, [factory, companyId, savingKey, approvedMappings]);

  const approveMappingRef = React.useRef(approveMapping);
  React.useEffect(() => { approveMappingRef.current = approveMapping; }, [approveMapping]);

  // Automatically resolve only cases where the deterministic matcher and AI agree
  // on one unique exact product code/name. All other cases remain in the review queue.
  React.useEffect(() => {
    if (!companyId || !items.length || !productCatalog.length || !factory.ok) return;
    const pending = items.filter((item) => !autoAiAttemptedRef.current.has(item.key)).slice(0, 40);
    if (!pending.length) return;
    const attemptKey = pending.map((item) => item.key).sort().join("|");
    if (autoAiAttemptedRef.current.has(attemptKey)) return;
    autoAiAttemptedRef.current.add(attemptKey);
    pending.forEach((item) => autoAiAttemptedRef.current.add(item.key));
    void (async () => {
      const result = await matchProductsWithAI(pending.map((item) => ({
        sourceId: item.key,
        code: item.code,
        name: item.name,
        quantity: Number(item.row.normalized_payload?.net_qty ?? item.row.normalized_payload?.inventory_qty ?? 0),
        unitCost: Number(item.row.normalized_payload?.dealer_purchase_price ?? item.row.normalized_payload?.cost_price ?? 0),
      })), companyId);
      if (!result.ok) {
        // Keep the queue intact; a transient AI/network error must never hide a row.
        autoAiAttemptedRef.current.delete(attemptKey);
        pending.forEach((item) => autoAiAttemptedRef.current.delete(item.key));
        setAiError(result.message);
        return;
      }
      let applied = 0;
      for (const proposal of result.matches) {
        if (!proposal.autoMatch || proposal.reviewRequired || !proposal.productId) continue;
        const item = pending.find((candidate) => candidate.key === proposal.sourceId);
        const product = productCatalog.find((candidate) => candidate.id === proposal.productId);
        if (!item || !product) continue;
        await approveMappingRef.current(item, product, "ai_auto", proposal.confidence, proposal.reason);
        applied += 1;
      }
      if (applied > 0) {
        setAutoMatchSummary(`${applied} ürün, sistem ve AI'nin aynı tekil eşleşmede anlaşmasıyla otomatik kaydedildi. Belirsiz satırlar inceleme kuyruğunda bırakıldı.`);
      }
    })();
  }, [items, productCatalog, companyId, factory]);

  const filteredItems = React.useMemo(() => {
    const query = normalized(search.trim());
    if (!query) return items;
    return items.filter((item) => [item.code, item.barcode, item.name, item.batch.original_filename ?? ""].some((value) => normalized(value).includes(query)));
  }, [items, search]);

  const selected = filteredItems.find((item) => item.key === selectedKey) ?? filteredItems[0] ?? null;

  const requestAiMatch = async () => {
    if (!selected || !companyId || aiMatching) return;
    setAiMatching(true); setAiError(null); setAiProposal(null);
    const result = await matchProductsWithAI([{
      sourceId: selected.key,
      code: selected.code,
      name: selected.name,
      quantity: Number(selected.row.normalized_payload?.net_qty ?? selected.row.normalized_payload?.inventory_qty ?? 0),
      unitCost: Number(selected.row.normalized_payload?.cost_price ?? 0),
    }], companyId);
    if (!result.ok) setAiError(result.message);
    else {
      const proposal = result.matches.find((item) => item.sourceId === selected.key);
      if (proposal) setAiProposal(proposal);
      else setAiError("AI bu satır için doğrulanabilir bir aday üretemedi; eşleşme yapılmadı.");
    }
    setAiMatching(false);
  };

  const exportQueue = () => {
    const rows = filteredItems.map((item) => ({
      "Kaynak dosya": item.batch.original_filename ?? "",
      "Aktarım tarihi": new Date(item.batch.created_at).toLocaleString("tr-TR"),
      "Excel satırı": item.row.source_row_number,
      "Kaynak ürün kodu": item.code,
      "Barkod": item.barcode,
      "Ürün adı": item.name,
      "Olası ürün kodları": item.candidates.map((candidate) => candidate.product_code).join(", "),
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), "Eşleme Kuyruğu");
    XLSX.writeFile(workbook, "ifm-urun-esleme-kuyrugu.xlsx");
  };

  if (!factory.ok) {
    return <div data-testid="page-product-matching" className="space-y-8"><PageHeader eyebrow="Veri İşleme" title="Ürün Eşleştirme" description="İçe aktarılan ürün kodlarını şirket ürün kataloğuyla karşılaştırın." /><div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">Supabase yapılandırması eksik; eşleme kuyruğu yüklenemiyor.</div></div>;
  }

  return (
    <div data-testid="page-product-matching" className="space-y-8">
      <PageHeader
        eyebrow="Veri İşleme"
        title="Ürün Eşleştirme"
        description="Kaydedilmiş Nebim satış/stok satırlarını şirket ürün kataloğuyla karşılaştırın; kesin eşleşmeyen kodları gözden geçirin."
        actions={<><Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}><RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Yenile</Button><Button size="sm" onClick={exportQueue} disabled={loading || filteredItems.length === 0}><Download className="mr-2 h-4 w-4" /> Excel'e aktar</Button></>}
      />


      <section className="space-y-4 rounded-lg border border-border bg-card p-5" data-testid="saved-invoice-matching">
        <div>
          <h2 className="text-lg font-semibold">Kaydedilmiş Beşiktaş faturaları</h2>
          <p className="mt-1 text-sm text-muted-foreground">Taslak olarak kaydedilmiş gerçek faturaların kalemlerini burada eşleştirin. Eşleştirme kalıcıdır; stok veya cari hareketi oluşturmaz.</p>
        </div>
        {autoMatchingInvoices ? <p role="status" className="rounded-md border border-border p-3 text-sm">Ürün kodu, model kodu ve ürün adı kontrol ediliyor; güvenli eşleşmeler otomatik kaydediliyor…</p> : null}
        {autoMatchSummary ? <p role="status" className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">{autoMatchSummary}</p> : null}
        {savedInvoiceError ? <div role="alert" className="rounded-md border border-destructive/40 p-3 text-sm text-destructive">{savedInvoiceError}</div> : null}
        {savedInvoices.length === 0 ? <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">Kesinleştirilmemiş kayıtlı gerçek fatura bulunamadı.</p> : null}
        {savedInvoices.map((invoice) => (
          <div key={invoice.id} className="space-y-3 rounded-md border border-border p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div><div className="font-medium">{invoice.invoice_no || "Fatura numarası yok"} · {invoice.supplier_name || "Tedarikçi bilinmiyor"}</div><div className="text-xs text-muted-foreground">{invoice.invoice_date || "Tarih yok"} · {invoice.lines.length} kalem · {invoice.status}</div></div>
              <div className="flex flex-col items-end gap-2">
                <div className="text-sm font-medium">{Number(invoice.grand_total || 0).toLocaleString("tr-TR",{style:"currency",currency:"TRY"})}</div>
                <div className="text-xs text-muted-foreground">{(invoice.lines ?? []).filter((line: any) => line.product_id).length}/{(invoice.lines ?? []).length} ürün eşleşti</div>
              </div>
            </div>
            <div className="space-y-2">
            {invoice.lines.map((line: any) => {
              const resolved = Boolean(line.product_id && ["manual", "matched"].includes(line.mapping_status));
              return <div key={line.id} className="rounded-md border border-border p-3">
                <div className="text-sm font-medium">{line.line_number}. {line.product_name || "Ürün adı yok"}</div>
                <div className="text-xs text-muted-foreground">Kaynak kod: {line.source_product_code || "—"} · Miktar: {line.quantity ?? "—"}</div>
                <div className={resolved ? "mt-1 text-xs text-emerald-700" : "mt-1 text-xs text-amber-700"}>{resolved ? "Otomatik eşleşti — maliyet ve adet ürün düzeyinde izlenir" : "Eşleşme bekliyor — tekil ürün kodu/model kodu/adı bulunamadı"}</div>
              </div>;
            })}
            </div>
          </div>
        ))}
      </section>

      {invoiceDraft?.invoice ? (() => {
        const lines = invoiceDraft.invoice.lines as any[];
        const issues = lines.map((line) => {
          const product = productCatalog.find((item) => item.product_code === line.matched_product_code);
          const problem = !product;
          const reason = !line.matched_product_code
            ? "Ürün eşleşmedi"
            : !product
              ? "Seçilen ürün katalogda bulunamadı"
              : "";
          return { line, product, problem, reason };
        }).filter((item) => item.problem);
        const allResolved = lines.length > 0 && issues.length === 0 && productCatalog.length > 0;
        return (
          <section className="space-y-4 rounded-lg border border-border bg-card p-5" data-testid="invoice-draft-matching">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Fatura ürün eşleştirme</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {invoiceDraft.invoice.meta?.invoice_no ?? invoiceDraft.invoice.file_name} · {lines.length} satır kontrol edildi.
                </p>
              </div>
              <div className={`rounded-full px-3 py-1.5 text-sm font-semibold ${allResolved ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>
                {loading ? "Kontrol ediliyor…" : allResolved ? "Ürün eşleşmesi %100" : `${lines.length - issues.length}/${lines.length} ürün eşleşti`}
              </div>
            </div>
            {loading ? (
              <div className="flex items-center gap-2 py-5 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Ürün ve varyantlar kontrol ediliyor…</div>
            ) : allResolved ? (
              <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                <div><div className="font-semibold">Tüm fatura satırları ürün düzeyinde eşleşti.</div><p className="mt-1">Miktar ve maliyet ürün koduna bağlanır; renk ve beden zorunlu değildir. Fatura kesinleştirme ve cari aktarımı ayrı, kontrollü adımlardır.</p></div>
              </div>
            ) : (
              <>
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Yalnızca ürün kodu güvenle belirlenemeyen satırlar gösteriliyor. Eşleşen satırlar gizlidir; kayıtlı seçimler otomatik olarak değiştirilmez.
                </div>
                <div className="space-y-3">
                  {issues.map(({ line, product, reason }) => {
                    const editing = editingInvoiceLineNo === line.line_no;
                    return (
                      <div key={line.line_no} className="rounded-md border border-amber-200 p-3" data-testid={`invoice-issue-${line.line_no}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold">{line.line_no}. {line.product_name || "Ürün adı yok"}</div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              Kaynak kod: {line.product_code || "Yok"} · Miktar: {line.qty ?? "—"}
                            </div>
                            <div className="mt-1 text-sm text-amber-800">{reason}</div>
                            {line.matched_product_code ? <div className="mt-1 text-xs text-muted-foreground">Seçili ürün: {line.matched_product_code}</div> : null}
                          </div>
                          <Button variant={editing ? "secondary" : "outline"} size="sm" onClick={() => setEditingInvoiceLineNo(editing ? null : line.line_no)}>
                            <Pencil className="mr-2 h-4 w-4" />{editing ? "Düzenlemeyi kapat" : "Düzenle"}
                          </Button>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => void requestInvoiceLineAiMatch(line)} disabled={invoiceAiBusyLine != null}>
                            {invoiceAiBusyLine === line.line_no ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Ürün adından AI ile bul
                          </Button>
                          {invoiceAiErrors[line.line_no] ? <span role="alert" className="text-xs text-amber-700">{invoiceAiErrors[line.line_no]}</span> : null}
                        </div>
                        {invoiceAiProposals[line.line_no] ? (() => {
                          const proposal = invoiceAiProposals[line.line_no];
                          const candidate = productCatalog.find((item) => item.id === proposal.productId);
                          return <div className="mt-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
                            <div className="text-xs text-muted-foreground">AI önerisi · güven skoru {(proposal.confidence * 100).toFixed(0)}%</div>
                            <p className="mt-1">{proposal.reason}</p>
                            {candidate ? <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                              <span className="font-medium">{candidate.product_code} — {candidate.product_name || "Ürün adı yok"}</span>
                              <Button size="sm" onClick={() => { assignInvoiceLine(line.line_no, candidate.product_code); setInvoiceAiProposals((current) => { const next = { ...current }; delete next[line.line_no]; return next; }); }}>Öneriyi onayla</Button>
                            </div> : <p className="mt-2 text-xs text-amber-700">Güvenilir ürün seçilemedi. Manuel inceleme gerekli.</p>}
                            <p className="mt-2 text-xs text-muted-foreground">AI önerisi otomatik kaydedilmez; onayladığında yalnızca fatura taslağına eşleme kaydedilir.</p>
                          </div>;
                        })() : null}
                        {editing ? (
                          <div className="mt-3 max-w-2xl space-y-2">
                            <label className="space-y-1 text-xs font-medium text-muted-foreground">
                              Doğru ürün
                              <select aria-label={`Fatura kalemi ${line.line_no} için ürün seç`} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
                                value={line.matched_product_code || ""}
                                onChange={(event) => assignInvoiceLine(line.line_no, event.target.value)}>
                                <option value="">Ürün seç…</option>
                                {productCatalog.map((item) => <option key={item.id} value={item.product_code}>{item.product_code} — {item.product_name || "Ürün adı yok"}</option>)}
                              </select>
                            </label>
                            <p className="text-xs text-muted-foreground">Ürün, adet ve maliyet ana ürün düzeyinde eşleştirilir; beden ve renk dikkate alınmaz.</p>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        );
      })() : null}

      {success ? <div role="status" className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-950">{success}</div> : null}
      {error ? <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"><AlertCircle className="mr-2 inline h-4 w-4" />{error}</div> : null}

      {!invoiceDraft?.invoice ? (
      <>
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
        <strong>Onay kontrollü eşleme:</strong> Kod/model eşleşmesi ve AI aynı tekil ürünü doğrularsa otomatik kaydedilir. Yalnızca çözülemeyen veya çelişkili ürünler inceleme kuyruğunda kalır; renk ve beden maliyet eşleşmesini engellemez.
      </div>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-5"><div className="text-sm text-muted-foreground">Eşlenmemiş ürün</div><div className="mt-2 text-2xl font-medium">{loading ? "…" : items.length.toLocaleString("tr-TR")}</div></div>
        <div className="rounded-lg border border-border bg-card p-5"><div className="text-sm text-muted-foreground">Aday önerisi olan</div><div className="mt-2 text-2xl font-medium">{loading ? "…" : items.filter((item) => item.candidates.length > 0).length.toLocaleString("tr-TR")}</div></div>
        <div className="rounded-lg border border-border bg-card p-5"><div className="text-sm text-muted-foreground">Son kontrol</div><div className="mt-2 text-sm font-medium">{lastLoaded ? new Date(lastLoaded).toLocaleString("tr-TR") : "Henüz çalıştırılmadı"}</div></div>
      </section>

      <section className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div data-testid="unmatched-panel" className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-5 py-4"><h2 className="font-serif text-xl">Eşlenmemiş satırlar</h2><p className="text-sm text-muted-foreground">Son {MAX_BATCHES} kaydedilmiş satış/stok aktarımında ürün kodu katalogda bulunmayan satırlar. Örnek/test dosyaları da dahil olabilir.</p></div>
          <div className="space-y-3 p-4">
            <div className="relative"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Kod, barkod, ürün adı veya dosya ara" className="pl-9" aria-label="Eşlenmemiş ürünlerde ara" /></div>
            {loading ? <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Eşleme kuyruğu yükleniyor…</div> : null}
            {!loading && !error && filteredItems.length === 0 ? <div className="p-8 text-center"><CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" /><p className="mt-3 font-medium">Bekleyen eşleme bulunamadı</p><p className="mt-1 text-sm text-muted-foreground">Kaydedilmiş satış/stok aktarımlarında bekleyen eşleme bulunmadı. Örnek/test dosyaları kullanıldıysa bu sonuç gerçek İFM verisinin doğrulandığı anlamına gelmez.</p></div> : null}
            <div className="max-h-[620px] space-y-2 overflow-auto">
              {filteredItems.map((item) => <button key={item.key} type="button" onClick={() => setSelectedKey(item.key)} className={`w-full rounded-md border p-3 text-left transition-colors ${selected?.key === item.key ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"}`}>
                <div className="flex items-start justify-between gap-3"><span className="font-mono text-sm">{item.code}</span><span className="shrink-0 text-xs text-muted-foreground">Satır {item.row.source_row_number}</span></div>
                <div className="mt-1 text-sm">{item.name || "Ürün adı yok"}</div>
                <div className="mt-1 text-xs text-muted-foreground">{[item.colorName || item.colorCode, item.sizeCode ? `Beden: ${item.sizeCode}` : ""].filter(Boolean).join(" · ") || "Varyant bilgisi yok"}</div>
                <div className="mt-1 text-xs text-muted-foreground">{item.barcode || "Barkod yok"} · {item.batch.original_filename || "Dosya adı yok"}</div>
              </button>)}
            </div>
          </div>
        </div>

        <div data-testid="candidates-panel" className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-5 py-4"><h2 className="font-serif text-xl">Olası eşlemeler</h2><p className="text-sm text-muted-foreground">Ürün kodu, model ve ürün ailesi benzerliğine göre adaylar. AI ve sistem aynı tekil ürünü doğruladığında otomatik kaydedilir; belirsiz eşleşmeler incelemede kalır.</p></div>
          <div className="space-y-4 p-5">
            {!selected ? <div className="py-10 text-center text-sm text-muted-foreground"><GitCompareArrows className="mx-auto mb-3 h-8 w-8" />Soldan bir ürün seçin.</div> : <>
              <div className="rounded-md bg-secondary/50 p-4"><div className="text-xs text-muted-foreground">Kaynak ürün</div><div className="mt-1 font-mono font-medium">{selected.code}</div><div className="mt-1 text-sm">{selected.name || "Ürün adı yok"}</div><div className="mt-1 text-xs text-muted-foreground">{[selected.colorName || selected.colorCode, selected.sizeCode ? `Beden: ${selected.sizeCode}` : ""].filter(Boolean).join(" · ") || "Varyant bilgisi yok"}</div><div className="mt-1 text-xs text-muted-foreground">Barkod: {selected.barcode || "—"}</div></div>
              {selected.candidates.length ? selected.candidates.map((candidate) => {  return <div key={candidate.id} className="rounded-md border border-border p-4"><div className="flex items-center gap-2 text-sm font-medium"><GitCompareArrows className="h-4 w-4 text-muted-foreground" />{candidate.product_code}</div><p className="mt-1 text-sm text-foreground">{candidate.product_name || "Ürün adı yok"}</p><p className="mt-1 text-xs text-muted-foreground">Ana ürün önerisidir; doğru ürünü seçip onaylayın. Beden/renk gerekmez.</p><Button className="mt-3" size="sm" onClick={() => void approveMapping(selected, candidate)} disabled={!!savingKey}>{savingKey === selected.key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}Bu eşlemeyi onayla</Button></div>; }) : <div className="rounded-md border border-border p-5 text-sm text-muted-foreground">Kod veya ürün adından yeterli benzerlikte aday bulunamadı. Kaynak adı ve katalog kontrol edilmelidir.</div>}
              <div className="rounded-md border border-primary/30 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div><div className="font-medium text-sm">Cloudflare AI ürün analizi</div><p className="mt-1 text-xs text-muted-foreground">AI; ürün ailesi, model kodu ve açıklamayı inceler. Miktar/maliyet yalnızca destekleyici kanıttır.</p></div>
                  <Button size="sm" variant="outline" onClick={() => void requestAiMatch()} disabled={aiMatching || !companyId}>{aiMatching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <GitCompareArrows className="mr-2 h-4 w-4" />}AI ile analiz et</Button>
                </div>
                {aiError ? <p role="alert" className="mt-3 text-sm text-destructive">{aiError}</p> : null}
                {aiProposal ? <div className="mt-3 rounded-md bg-secondary/50 p-3">
                  <div className="text-xs text-muted-foreground">AI değerlendirmesi · güven skoru {(aiProposal.confidence * 100).toFixed(0)}%</div>
                  <p className="mt-1 text-sm">{aiProposal.reason}</p>
                  {aiProposal.productId ? (() => {
                    const proposed = productCatalog.find((product) => product.id === aiProposal.productId);
                    return proposed ? <div className="mt-3 flex flex-wrap items-center justify-between gap-2"><div className="text-sm font-medium">{proposed.product_code} — {proposed.product_name || "Ürün adı yok"}</div><Button size="sm" onClick={() => void approveMapping(selected, proposed)} disabled={!!savingKey}>AI önerisini inceleyip onayla</Button></div> : <p className="mt-2 text-sm text-amber-700">AI adayı katalogda artık aktif değil; kayıt yapılmadı.</p>;
                  })() : <p className="mt-2 text-sm text-amber-700">AI güvenilir bir ürün kimliği belirleyemedi. Manuel inceleme gerekli.</p>}
                  <p className="mt-2 text-xs text-muted-foreground">Otomatik eşleşme yalnızca tekil ürün kodu/adı kanıtı ve en az %95 AI güveniyle yapılır; stok ve cari hareketi oluşturulmaz.</p>
                </div> : null}
              </div>
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-950"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /><span>Bu öneriler kod ve ürün adı benzerliğine dayanır; doğru ana ürünü seçip onayla. Beden ve renk seçmen gerekmez. Onay, geçmiş satış veya stok satırlarını geriye dönük değiştirmez.</span></div>
            </>}
          </div>
        </div>
      </section>

      <section aria-label="Eşleme denetim geçmişi" className="rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-serif text-xl">Onaylanmış kalıcı eşlemeler</h2>
          <span className="text-sm text-muted-foreground">{approvedMappings.length.toLocaleString("tr-TR")} kayıt yüklendi (en fazla 500)</span>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">Bu liste seçili şirketin Supabase kayıtlarından yüklenir. Eşlemeler ana ürün düzeyindedir; aynı ürünün tüm beden ve renkleri ortak maliyet altında değerlendirilir.</p>
        {approvedMappings.length === 0 ? (
          <p className="mt-4 rounded-md border border-dashed border-border p-5 text-sm text-muted-foreground">Henüz onaylanmış Nebim eşlemesi yok.</p>
        ) : (
          <div className="mt-4 max-h-80 overflow-auto rounded-md border border-border">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-secondary/80"><tr><th className="p-3 font-medium">Kaynak kod</th><th className="p-3 font-medium">Kaynak ürün</th><th className="p-3 font-medium">Eşlenen ürün kodu</th><th className="p-3 font-medium">Son güncelleme</th></tr></thead>
              <tbody>
                {approvedMappings.map((mapping) => {
                  const target = mapping.product_id ? productCodeById.get(mapping.product_id) : null;
                  return <tr key={mapping.id} className="border-t border-border"><td className="p-3 font-mono">{mapping.source_product_code}</td><td className="p-3">{mapping.source_product_name || mapping.source_barcode || "—"}</td><td className="p-3 font-mono">{target || "Ürün bulunamadı"}</td><td className="p-3 text-muted-foreground">{mapping.updated_at ? new Date(mapping.updated_at).toLocaleString("tr-TR") : "—"}</td></tr>;
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 flex items-start gap-2 text-sm text-muted-foreground"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />Kalıcı eşlemeler ürün kataloğunu değiştirmez. Maliyet ve kârlılık hesapları ana ürün kimliği üzerinden yapılır.</div>
      </section>
      </>
      ) : null}
    </div>
  );
};

export default ProductMatching;
